import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Divider,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Chip,
  Fade,
  alpha,
} from '@mui/material';
import {
  Google as GoogleIcon,
  Apple as AppleIcon,
  Public as PublicIcon,
  AutoAwesome as AIIcon,
  Verified as VerifiedIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { API_BASE_URL } from '../../config/api';
import { ssoBackendOrigin } from '../../lib/ssoBackend';
import TurnstileBox, { isTurnstileEnabled } from '../../components/Auth/TurnstileBox';

// Production city backgrounds — exact from live tripnovac-visa.com bundle
const BACKGROUNDS = [
  { url: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=95', location: 'Paris, France', tagline: 'Your Journey Starts Here', subtitle: 'Seamless visa applications, intelligent travel planning, and enterprise-grade booking management.' },
  { url: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=95', location: 'London, United Kingdom', tagline: 'Explore Without Limits', subtitle: 'AI-powered visa processing for 249 countries and territories (ISO 3166-1).' },
  { url: 'https://images.unsplash.com/photo-1538332576228-eb5b4c4de6f5?w=1920&q=95', location: 'Dubai, UAE', tagline: 'Global Reach, Structured Preparation', subtitle: 'Account workflows for individual and organizational visa preparation.' },
  { url: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=1920&q=95', location: 'New York, USA', tagline: 'Travel Smarter', subtitle: 'Real-time visa updates and intelligent document verification.' },
  { url: 'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=1920&q=95', location: 'Sydney, Australia', tagline: 'Simplify Your Travels', subtitle: 'From visa application to arrival \u2014 we handle everything.' },
  { url: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1920&q=95', location: 'Tokyo, Japan', tagline: 'Innovation Meets Travel', subtitle: 'Cutting-edge technology for seamless global mobility.' },
  { url: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1920&q=95', location: 'Rome, Italy', tagline: 'Prepare With Clarity', subtitle: 'Organize visa research, application records, and travel documents.' },
  { url: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1920&q=95', location: 'Singapore', tagline: 'Organize Your Next Step', subtitle: 'Visa research, document organization, and application preparation in one account.' },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading, isAuthenticated, user } = useAuth();
  const { showError, showSuccess } = useNotification();

  // Redirect already-authenticated users
  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(user.isEmailVerified ? '/dashboard' : '/verify-email', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // SSO redirect — sends user to backend Cognito flow
  const handleSSOLogin = (provider: 'google' | 'apple') => {
    const backendBase = ssoBackendOrigin(API_BASE_URL);
    window.location.href = `${backendBase}/api/v1/auth/sso/${provider}`;
  };

  /** Platform Hub SSO — user signs in on Hub, returns to Visa /auth/sso with token */
  const handleHubSSOLogin = () => {
    const backendBase = ssoBackendOrigin(API_BASE_URL);
    window.location.href = `${backendBase}/api/v1/sso/login/platform-hub`;
  };

  // Show SSO error if returned from Cognito/Hub with error param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoError = params.get('error');
    if (ssoError) {
      const reason = params.get('reason');
      const friendly: Record<string, string> = {
        missing_token: 'Open Visa from Platform Hub, or tap Continue with Tripnovac Hub.',
        invalid_token: 'Hub sign-in token was invalid. Try again from Platform Hub.',
        invalid_token_claims: 'Hub token claims were rejected. Try again from Platform Hub.',
        wrong_audience: 'This Hub session was not issued for Tripnovac Visa.',
        wrong_issuer: 'Unexpected Hub issuer. Contact support if this continues.',
        hub_unavailable: 'Platform Hub is temporarily unavailable. Use email or Google/Apple.',
        replay: 'That Hub sign-in link was already used. Open Visa again from Hub.',
        invalid_source: 'Invalid SSO source.',
        sso_failed: 'Hub sign-in failed. Please try again.',
        sso_exchange_failed: 'Google or Apple sign-in could not finish. Try again, or use email.',
        missing_sso_config: 'Sign-in is temporarily unavailable. Use email login.',
      };
      const decoded = decodeURIComponent(ssoError);
      const friendlyKey = decoded.split(':')[0];
      showError(
        friendly[decoded] ||
          friendly[friendlyKey] ||
          `SSO login failed: ${decoded}${reason ? ` (${decodeURIComponent(reason)})` : ''}`
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [currentBg, setCurrentBg] = useState(() => Math.floor(Math.random() * BACKGROUNDS.length));
  const [nextBg, setNextBg] = useState<number | null>(null);
  const [fading, setFading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loginError, setLoginError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  // Preload all background images
  useEffect(() => {
    BACKGROUNDS.forEach(bg => { const img = new Image(); img.src = bg.url; });
  }, []);

  // Rotate backgrounds every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setNextBg((currentBg + 1) % BACKGROUNDS.length);
      setFading(true);
      setTimeout(() => {
        setCurrentBg(prev => (prev + 1) % BACKGROUNDS.length);
        setFading(false);
        setNextBg(null);
      }, 1000);
    }, 8000);
    return () => clearInterval(interval);
  }, [currentBg]);

  const bg = BACKGROUNDS[currentBg];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = useCallback(() => {
    const e: Record<string, string> = {};
    if (!formData.email.trim()) e.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Please enter a valid email address';
    if (!formData.password) e.password = 'Password is required';
    else if (formData.password.length < 8) e.password = 'Password must be at least 8 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [formData]);

  const handleLogin = async (email?: string, pwd?: string) => {
    const loginEmail = email || formData.email;
    const loginPwd = pwd || formData.password;

    if (!email && !validate()) return;
    if (isTurnstileEnabled() && !turnstileToken) {
      setLoginError('Complete the security check and try again.');
      showError('Complete the security check and try again.');
      return;
    }

    try {
      const result = await login(loginEmail, loginPwd, turnstileToken);
      if (result.success && (result as any).user) {
        const u = (result as any).user;
        if (!u.isEmailVerified) {
          showSuccess('Please verify your email to continue.');
          setTimeout(() => navigate('/verify-email', { replace: true }), 400);
          return;
        }
        showSuccess('Welcome back! Redirecting to your dashboard...');
        setTimeout(() => navigate('/dashboard', { replace: true }), 500);
      } else if (result.success) {
        showSuccess('Login successful!');
        navigate('/dashboard', { replace: true });
      } else {
        setLoginError(result.message || 'Login failed. Please check your credentials.');
        showError(result.message || 'Login failed. Please check your credentials.');
      }
    } catch {
      setLoginError('An unexpected error occurred. Please try again.');
      showError('An unexpected error occurred. Please try again.');
    }
  };

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); handleLogin(); };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', position: 'relative', overflow: 'hidden', bgcolor: '#0F172A' }}>
      {/* Rotating Background */}
      <Box sx={{ position: 'absolute', inset: 0, zIndex: 0 }}>
        <Box sx={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${bg.url})`, backgroundSize: 'cover', backgroundPosition: 'center',
          transition: 'opacity 1s ease-in-out', opacity: fading ? 0 : 1,
          '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(15,23,42,0.7) 0%, rgba(15,23,42,0.4) 50%, rgba(15,23,42,0.6) 100%)' },
        }} />
        {nextBg !== null && (
          <Box sx={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${BACKGROUNDS[nextBg].url})`, backgroundSize: 'cover', backgroundPosition: 'center',
            transition: 'opacity 1s ease-in-out', opacity: fading ? 1 : 0,
            '&::after': { content: '""', position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(15,23,42,0.7) 0%, rgba(15,23,42,0.4) 50%, rgba(15,23,42,0.6) 100%)' },
          }} />
        )}
      </Box>

      {/* Top Bar — Feature Chips + Stats */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1.5 }}>
          {[
            { icon: <PublicIcon sx={{ fontSize: 16 }} />, text: '249 countries' },
            { icon: <AIIcon sx={{ fontSize: 16 }} />, text: 'AI-Powered' },
            { icon: <VerifiedIcon sx={{ fontSize: 16 }} />, text: 'Premium' },
          ].map((item, i) => (
            <Chip key={i} icon={item.icon} label={item.text} size="small"
              sx={{ bgcolor: alpha('#FFFFFF', 0.15), backdropFilter: 'blur(10px)', color: 'white', fontWeight: 500, fontSize: '0.75rem', '& .MuiChip-icon': { color: 'white' }, border: `1px solid ${alpha('#FFFFFF', 0.2)}` }}
            />
          ))}
        </Box>
        <Box sx={{ display: { xs: 'none', lg: 'flex' }, gap: 4, color: 'white' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700}>Online</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Support Requests</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700}>100%</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>Secure</Typography>
          </Box>
        </Box>
      </Box>

      {/* Login Card */}
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, position: 'relative', p: { xs: 2, md: 4 } }}>
        <Fade in timeout={500}>
          <Box sx={{
            width: '100%', maxWidth: 400,
            background: 'linear-gradient(145deg, #1a1f3c 0%, #0d1025 50%, #141832 100%)',
            backdropFilter: 'blur(20px)', borderRadius: 3, p: { xs: 2.5, sm: 3 },
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          }}>
            {/* Logo */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box component="img" src="/assets/app-icon.png" alt="Tripnovac Visa"
                sx={{ height: 64, width: 'auto', mb: 1 }}
                onError={(e: any) => { e.target.style.display = 'none'; }}
              />
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                Tripnovac Visa
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                Enterprise Visa Processing Platform
              </Typography>
            </Box>

            {/* Login Error */}
            {loginError && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: alpha('#EF4444', 0.15), border: `1px solid ${alpha('#EF4444', 0.3)}` }}>
                <Typography variant="caption" sx={{ color: '#FCA5A5' }}>{loginError}</Typography>
              </Box>
            )}

            {/* Form */}
            <Box component="form" onSubmit={handleSubmit}>
              <TextField fullWidth placeholder="Email address" name="email" type="email"
                value={formData.email} onChange={handleChange}
                error={!!errors.email} helperText={errors.email}
                autoComplete="email" size="small"
                sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' }, '&.Mui-focused fieldset': { borderColor: '#0EA5E9' } }, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' }, '& .MuiFormHelperText-root': { color: '#FCA5A5' } }}
              />
              <TextField fullWidth placeholder="Password" name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password} onChange={handleChange}
                error={!!errors.password} helperText={errors.password}
                autoComplete="current-password" size="small"
                InputProps={{
                  endAdornment: (
                    <Box
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ cursor: 'pointer', display: 'flex', color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
                    >
                      {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                    </Box>
                  ),
                }}
                sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.25)' }, '&.Mui-focused fieldset': { borderColor: '#0EA5E9' } }, '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)' }, '& .MuiFormHelperText-root': { color: '#FCA5A5' } }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <FormControlLabel
                  control={<Checkbox name="rememberMe" checked={formData.rememberMe} onChange={handleChange} size="small" sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#0EA5E9' } }} />}
                  label={<Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>Remember me</Typography>}
                />
                <Link component={RouterLink} to="/forgot-password" sx={{ color: '#0EA5E9', textDecoration: 'none', fontSize: '0.75rem', fontWeight: 500 }}>
                  Forgot password?
                </Link>
              </Box>

              <TurnstileBox onToken={setTurnstileToken} />

              <Button type="submit" fullWidth variant="contained" disabled={isLoading}
                sx={{ py: 1.2, borderRadius: 2, fontWeight: 600, fontSize: '0.9rem', textTransform: 'none', background: 'linear-gradient(135deg, #0EA5E9 0%, #14B8A6 100%)', boxShadow: '0 4px 20px rgba(14, 165, 233, 0.3)', '&:hover': { background: 'linear-gradient(135deg, #0284C7 0%, #0D9488 100%)', boxShadow: '0 10px 40px rgba(14, 165, 233, 0.4)' }, '&:disabled': { background: '#CBD5E1' } }}>
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
              </Button>
            </Box>

            {/* Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', my: 1.5 }}>
              <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
              <Typography variant="caption" sx={{ px: 1.5, color: 'rgba(255, 255, 255, 0.6)' }}>or</Typography>
              <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }} />
            </Box>

            {/* Platform Hub SSO */}
            <Button
              fullWidth
              variant="contained"
              startIcon={<PublicIcon sx={{ fontSize: 18 }} />}
              onClick={handleHubSSOLogin}
              sx={{
                mb: 1,
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.85rem',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 100%)',
                border: '1px solid rgba(14, 165, 233, 0.45)',
                color: '#E0F2FE',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0B1220 0%, #164E63 100%)',
                  borderColor: '#38bdf8',
                },
              }}
            >
              Continue with Tripnovac Hub
            </Button>

            {/* Cognito SSO Buttons */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button fullWidth variant="outlined" startIcon={<GoogleIcon sx={{ fontSize: 18 }} />}
                onClick={() => handleSSOLogin('google')}
                sx={{ py: 0.9, borderRadius: 2, borderColor: '#0EA5E9', color: '#0EA5E9', fontWeight: 500, fontSize: '0.8rem', bgcolor: alpha('#0EA5E9', 0.1), '&:hover': { borderColor: '#38bdf8', bgcolor: alpha('#0EA5E9', 0.2), color: '#38bdf8' } }}>
                Google
              </Button>
              <Button fullWidth variant="outlined" startIcon={<AppleIcon sx={{ fontSize: 18 }} />}
                onClick={() => handleSSOLogin('apple')}
                sx={{ py: 0.9, borderRadius: 2, borderColor: '#0EA5E9', color: '#0EA5E9', fontWeight: 500, fontSize: '0.8rem', bgcolor: alpha('#0EA5E9', 0.1), '&:hover': { borderColor: '#38bdf8', bgcolor: alpha('#0EA5E9', 0.2), color: '#38bdf8' } }}>
                Apple
              </Button>
            </Box>

            {/* Sign Up Link */}
            <Typography variant="caption" textAlign="center" display="block" sx={{ mb: 1.5, color: 'rgba(255, 255, 255, 0.8)' }}>
              Don't have an account?{' '}
              <Link component={RouterLink} to="/register" sx={{ color: '#0EA5E9', fontWeight: 600, textDecoration: 'none' }}>
                Sign up
              </Link>
            </Typography>

            {/* Security Badges */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, pt: 1.5, borderTop: '1px solid rgba(255, 255, 255, 0.15)' }}>
              {[
                { icon: <LockIcon sx={{ fontSize: 14 }} />, text: 'HTTPS' },
                { icon: <ShieldIcon sx={{ fontSize: 14 }} />, text: 'Role Access' },
                { icon: <VerifiedIcon sx={{ fontSize: 14 }} />, text: 'Stripe Payments' },
              ].map((badge, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ color: '#10B981' }}>{badge.icon}</Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }} fontWeight={500}>{badge.text}</Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1, fontSize: '0.65rem', color: 'rgba(255, 255, 255, 0.5)' }}>
              &copy; 2026 Tripnovac by Alpha Libra LLC
            </Typography>
          </Box>
        </Fade>
      </Box>

      {/* Bottom Right — Tagline */}
      <Box sx={{ position: 'absolute', bottom: 20, right: 20, zIndex: 10, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
          {bg.tagline}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 350, mt: 0.5 }}>
          Enterprise Visa Processing Platform
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
